import type { ReactNode } from 'react';

import type { MetricValues } from '@revelio/core';
import { type BarData, FormatType, getFormatter } from '@revelio/replots';
import { Text } from '@revelio/ui';

import type { SingleDataPoint } from '../../DataProvider/types';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { getFormat } from './getFormat';
import styles from './sentiment-count-tooltip.module.css';

type CountedDataPoint = SingleDataPoint<{ count?: number }>;

export const buildSentimentCountTooltip = ({
  data,
  item,
  metric,
}: {
  data: CountedDataPoint[];
  item?: PositionedItem;
  metric?: MetricValues;
}): ((d: BarData<{ count?: number }>) => ReactNode) | undefined => {
  const hasCount = data.some((p) => typeof p.count === 'number');
  if (!hasCount) return undefined;

  const format = item?.plot?.format ?? getFormat({ metric });

  const formatValue = getFormatter(
    format === FormatType.SI ? FormatType.INTEGER : format,
    {
      values: data.map((d) => d.value).filter((v): v is number => v !== null),
      increasePrecision: 1,
    }
  );

  return (d) => {
    const count = d.count;
    const valueText = d.value === null ? '-' : formatValue(d.value);
    return (
      <div>
        <Text weight="semibold" tone="inherit" className={styles.line}>
          {`${d.label}: ${valueText}`}
        </Text>
        {typeof count === 'number' && (
          <Text tone="inherit" className={styles.line}>{`(n: ${count})`}</Text>
        )}
      </div>
    );
  };
};
